/**
 * Pure delivery-date helpers for evergreen: report formatting + the order-cutoff rule.
 *
 * No DOM / window access — safe to require() in Node tests and to load in the
 * browser (attaches to window.StockedDeliveryFormat). All math uses UTC date
 * construction so it never drifts with the browser timezone; callers pass the
 * store-time calendar date (y, m, d) that delivery-date.js already generates.
 */
(function (root, factory) {
  var api = factory();
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  else root.StockedDeliveryFormat = api;
})(typeof self !== 'undefined' ? self : this, function () {
  var MONTHS = ['January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'];
  var MONTHS_ABBR = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  var WEEKDAYS_ABBR = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  var MS_PER_DAY = 86400000;

  function pad2(n) { return String(n).padStart(2, '0'); }

  function isoOf(y, m, d) { return y + '-' + pad2(m) + '-' + pad2(d); }

  // 1-based day index within the date's own year.
  function dayOfYear(y, m, d) {
    return Math.round((Date.UTC(y, m - 1, d) - Date.UTC(y, 0, 1)) / MS_PER_DAY) + 1;
  }

  // Weekday, 0 = Sun .. 6 = Sat.
  function weekday(y, m, d) {
    return new Date(Date.UTC(y, m - 1, d)).getUTCDay();
  }

  // Day-of-year of the year's first Sunday (start of strftime %U week 01).
  function firstSundayDOY(y) {
    var w = weekday(y, 1, 1);
    return w === 0 ? 1 : 8 - w;
  }

  // strftime %U: Sunday-based week of year, 0..53. Days before the first Sunday
  // are week 0.
  function weekU(y, m, d) {
    var n = dayOfYear(y, m, d);
    var fs = firstSundayDOY(y);
    return n < fs ? 0 : Math.floor((n - fs) / 7) + 1;
  }

  function deliveryWeekCode(y, m, d) {
    var year = y;
    var week = weekU(y, m, d);
    if (week === 0) {           // partial pre-first-Sunday week -> prior year's last week
      year = y - 1;
      week = weekU(year, 12, 31);
    }
    // Two-digit year assumes 2001-2099; year % 100 would collide at century boundaries.
    return 'DS ' + pad2(year % 100) + pad2(week);
  }

  function ordinal(n) {
    var t = n % 100;
    if (t >= 11 && t <= 13) return n + 'th';
    switch (n % 10) {
      case 1: return n + 'st';
      case 2: return n + 'nd';
      case 3: return n + 'rd';
      default: return n + 'th';
    }
  }

  // Label for the delivery window containing (y,m,d). Endpoints are the first and last
  // CONFIGURED delivery weekday of that week -- not a hardcoded Sun..Tue span.
  function deliveryWindowLabel(y, m, d, weekdays) {
    var wd = (Array.isArray(weekdays) && weekdays.length ? weekdays.slice() : [0, 1, 2])
      .sort(function (a, b) { return a - b; });
    var off = weekday(y, m, d);                        // days since the week's Sunday
    var start = new Date(Date.UTC(y, m - 1, d - off + wd[0]));
    var end = new Date(Date.UTC(y, m - 1, d - off + wd[wd.length - 1]));
    var sMonth = MONTHS[start.getUTCMonth()];
    var eMonth = MONTHS[end.getUTCMonth()];
    var sDay = ordinal(start.getUTCDate());
    var eDay = ordinal(end.getUTCDate());
    if (sMonth === eMonth) return sMonth + ' ' + sDay + ' - ' + eDay;
    return sMonth + ' ' + sDay + ' - ' + eMonth + ' ' + eDay;
  }

  function deliveryDueLabel(y, m, d) {
    return WEEKDAYS_ABBR[weekday(y, m, d)] + ', ' + d + ' ' + MONTHS_ABBR[m - 1] + ' ' + y;
  }

  // Short display label for the persistent delivery bar (snippets/delivery-bar.liquid)
  // -- weekday + month + ordinal day, no year, since the bar only ever shows a near-term
  // date. Kept separate from deliveryDueLabel, which also feeds the cart's "Order Due
  // Date" attribute (assets/delivery-date.js toAttributes()) and must not reformat.
  function deliveryBarDateLabel(y, m, d) {
    return WEEKDAYS_ABBR[weekday(y, m, d)] + ', ' + MONTHS_ABBR[m - 1] + ' ' + ordinal(d);
  }

  // True for a real calendar date in strict YYYY-MM-DD form (rejects e.g. 2026-02-30).
  function validISO(str) {
    if (typeof str !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(str)) return false;
    var p = str.split('-');
    var y = +p[0], m = +p[1], d = +p[2];
    var probe = new Date(Date.UTC(y, m - 1, d));
    return probe.getUTCFullYear() === y && probe.getUTCMonth() === m - 1 && probe.getUTCDate() === d;
  }

  // Order cutoff for a SINGLE delivery date: `daysBefore` days before THAT date, at
  // 12:30 PM store time. Per-date (not window-shared) — Sun/Mon/Tue of a window each get
  // their own cutoff. An explicit override in delivery_config.cutoff_overrides
  // (deliveryISO -> cutoff date "YYYY-MM-DD") wins over the computed value. `daysBefore`
  // comes from delivery_config (caller passes it via opts); defaults to 4. Returns
  // {y, m, d, hour, minute}.
  //
  // The cutoff TIME (12:30) is hardcoded here AND as the timeAfter("12:30:00") literal in
  // the validation function's input query (Shopify forbids a runtime variable there). It is
  // NOT stored in delivery_config; overrides change only the DATE. Change one, change both.
  function deliveryCutoff(y, m, d, opts) {
    opts = opts || {};
    var HOUR = 12, MINUTE = 30;
    var overrides = (opts.overrides && typeof opts.overrides === 'object'
      && !Array.isArray(opts.overrides)) ? opts.overrides : {};
    var ov = overrides[isoOf(y, m, d)];
    if (validISO(ov)) {                                  // malformed override -> computed cutoff (never throw)
      var p = ov.split('-');
      return { y: +p[0], m: +p[1], d: +p[2], hour: HOUR, minute: MINUTE };
    }
    var N = Number.isInteger(opts.daysBefore) ? opts.daysBefore : 4;
    var cut = new Date(Date.UTC(y, m - 1, d - N));       // per-date: cutoff is N days before THIS date
    return { y: cut.getUTCFullYear(), m: cut.getUTCMonth() + 1, d: cut.getUTCDate(), hour: HOUR, minute: MINUTE };
  }

  // "DS YYWW" -> {year, week}, or null for anything malformed. Week 0 is never
  // emitted by deliveryWeekCode (it rolls into the prior year's last week), so a
  // literal 00 is treated as malformed.
  function parseWeekCode(str) {
    if (typeof str !== 'string') return null;
    var m = /^DS (\d{2})(\d{2})$/.exec(str.trim());
    if (!m) return null;
    var week = +m[2];
    if (week < 1 || week > 53) return null;
    return { year: 2000 + (+m[1]), week: week };   // matches the 2001-2099 assumption above
  }

  function weekCodeOf(iso) {
    if (typeof iso !== 'string') return null;
    var m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso.slice(0, 10));
    if (!m) return null;
    return deliveryWeekCode(+m[1], +m[2], +m[3]);
  }

  // Catering-only delivery dates outside the regular cadence, from
  // delivery_config.special_dates: { iso: { label?, opens? } } -> { iso: { label, opens } }.
  // An entry is dropped if the key is not a real date, the value is not a plain object, or
  // the date falls on a regular delivery weekday (a typo there must not turn a regular day
  // into a one-off slot). A malformed opens/label is treated as absent. Never throws.
  function specialDatesOf(config) {
    var raw = config && typeof config === 'object' ? config.special_dates : null;
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {};
    var wd = Array.isArray(config.delivery_weekdays) && config.delivery_weekdays.length
      ? config.delivery_weekdays : [0, 1, 2];
    var out = {};
    Object.keys(raw).forEach(function (iso) {
      var e = raw[iso];
      if (!validISO(iso) || !e || typeof e !== 'object' || Array.isArray(e)) return;
      if (wd.indexOf(weekday(+iso.slice(0, 4), +iso.slice(5, 7), +iso.slice(8, 10))) !== -1) return;
      out[iso] = {
        label: typeof e.label === 'string' && e.label.trim() ? e.label.trim() : null,
        opens: validISO(e.opens) ? e.opens : null,
      };
    });
    return out;
  }

  function isSpecialDate(iso, config) {
    return typeof iso === 'string' && Object.prototype.hasOwnProperty.call(specialDatesOf(config), iso);
  }

  // Special and past its opens date (inclusive). Does NOT check blackout or cutoff.
  function specialDateOpen(iso, config, todayISO) {
    if (!isSpecialDate(iso, config)) return false;
    var opens = specialDatesOf(config)[iso].opens;
    return !opens || (typeof todayISO === 'string' && todayISO >= opens);
  }

  function specialDateLabel(iso, config) {
    return isSpecialDate(iso, config) ? specialDatesOf(config)[iso].label : null;
  }

  // "Wed, Nov 25": the short date special-date copy uses (badges, banners, dialogs),
  // the same shape as the cart's date.
  // null for anything that isn't a real ISO date.
  function shortDateLabel(iso) {
    if (!validISO(iso)) return null;
    var y = +iso.slice(0, 4), m = +iso.slice(5, 7), d = +iso.slice(8, 10);
    return WEEKDAYS_ABBR[weekday(y, m, d)] + ', ' + MONTHS_ABBR[m - 1] + ' ' + d;
  }

  // "Thanksgiving · Wed Nov 25", or just the short date when the date has no label.
  function specialDateTitle(iso, config) {
    var short = shortDateLabel(iso);
    if (!short) return null;
    var label = specialDateLabel(iso, config);
    return label ? label + ' \u00b7 ' + short : short;
  }

  // The delivery_window_label cart attribute for a chosen date: the date's Sun-Tue
  // window ("Nov 22nd - 24th"), or for a special date its own single day
  // ("Thanksgiving \u00b7 Wed Nov 25") -- a catering order never delivers across a
  // window. '' for anything that isn't a real ISO date.
  function selectionWindowLabel(iso, config, weekdays) {
    if (!validISO(iso)) return '';
    if (isSpecialDate(iso, config)) return specialDateTitle(iso, config) || '';
    return deliveryWindowLabel(+iso.slice(0, 4), +iso.slice(5, 7), +iso.slice(8, 10), weekdays);
  }

  // Whether a stored special-date selection should be dropped. It outlives the catering
  // cart (after checkout, or once the cart is emptied), and left in place it hides every
  // regular card on the menu. A blank menu is only right when the CART is special (spec
  // 5.2), so drop it when the cart has no non-exempt lines, except on the catering surface
  // itself. Never drops while the cart has lines.
  function shouldDropSpecialSelection(opts) {
    var o = opts || {};
    return !o.specialSurface && !o.cartLineCount && isSpecialDate(o.selectionISO, o.config);
  }

  // The availability unit a date belongs to: itself when special, else its week code.
  function slotOf(iso, config) {
    return isSpecialDate(iso, config) ? iso : weekCodeOf(iso);
  }

  // The week a regular menu surface renders for a selection. A special (catering)
  // selection has no menu of its own, so the menu shows as the first regular date's
  // week would (firstRegularISO: the earliest selectable regular date) -- nothing on
  // it is orderable for the special date, and adding goes through the remove-items
  // dialog. null with no selection, or no regular date to fall back on.
  function menuSlot(selectionISO, config, firstRegularISO) {
    if (!validISO(selectionISO)) return null;
    if (!isSpecialDate(selectionISO, config)) return weekCodeOf(selectionISO);
    return validISO(firstRegularISO) ? weekCodeOf(firstRegularISO) : null;
  }

  // The menu's notice for a cart set for a special date: "Your cart has Thanksgiving
  // items for Wed Nov 25. ..." strings: {label, noLabel} from locales
  // (delivery.special.cart_notice*), with {{ label }} / {{ date }} placeholders.
  // '' when the date isn't special or the copy is missing.
  function specialCartNotice(iso, config, strings) {
    if (!strings || !isSpecialDate(iso, config)) return '';
    var label = specialDateLabel(iso, config);
    var template = label ? strings.label : strings.noLabel;
    var vars = { label: label || '', date: shortDateLabel(iso) || '' };
    return String(template || '').replace(/\{\{\s*(\w+)\s*\}\}/g, function (all, key) {
      return Object.prototype.hasOwnProperty.call(vars, key) ? vars[key] : all;
    });
  }

  // Day-of-year of the Sunday that starts week W (inverse of weekU).
  function weekStartUTC(year, week) {
    return new Date(Date.UTC(year, 0, firstSundayDOY(year) + (week - 1) * 7));
  }

  function isoOfUTC(dt) {
    return isoOf(dt.getUTCFullYear(), dt.getUTCMonth() + 1, dt.getUTCDate());
  }

  // First and last DELIVERY day of a week code. Endpoints come from the configured
  // weekdays -- never hardcode Sun..Tue; delivery_weekdays is admin-configurable and a
  // hardcoded span would silently exclude real delivery days from an allowed week.
  function weekBounds(code, weekdays) {
    var p = parseWeekCode(code);
    if (!p) return null;
    var wd = (Array.isArray(weekdays) && weekdays.length ? weekdays.slice() : [0, 1, 2])
      .sort(function (a, b) { return a - b; });
    var sun = weekStartUTC(p.year, p.week);
    var ms = sun.getTime();
    return {
      firstISO: isoOfUTC(new Date(ms + wd[0] * MS_PER_DAY)),
      lastISO: isoOfUTC(new Date(ms + wd[wd.length - 1] * MS_PER_DAY)),
    };
  }

  function normaliseCode(v) {
    return parseWeekCode(v) ? String(v).trim() : null;
  }

  // Accepts an array (Liquid list metafield .value) or a JSON string (the function's
  // metafield .value). Anything unusable yields [] so the caller falls back to bounds.
  function normaliseWeekList(raw) {
    var list = raw;
    if (typeof list === 'string') {
      try { list = JSON.parse(list); } catch (e) { return []; }
    }
    if (!Array.isArray(list)) return [];
    var seen = {};
    var out = [];
    list.forEach(function (v) {
      var c = normaliseCode(v);
      if (c && !seen[c]) { seen[c] = 1; out.push(c); }
    });
    return out.sort();          // DS YYWW sorts lexicographically
  }

  // Same contract as normaliseWeekList, for ISO dates (list.date metafield).
  function normaliseDateList(raw) {
    var list = raw;
    if (typeof list === 'string') {
      try { list = JSON.parse(list); } catch (e) { return []; }
    }
    if (!Array.isArray(list)) return [];
    var seen = {};
    var out = [];
    list.forEach(function (v) {
      if (typeof v === 'string') v = v.trim();
      if (validISO(v) && !seen[v]) { seen[v] = 1; out.push(v); }
    });
    return out.sort();
  }

  // The single place the precedence rule lives: dates > weeks > start/end bounds.
  // `dates` is non-null whenever the raw list held at least one valid ISO, even if none is
  // a configured special date -- such a product must stay off the menu, not fall back to weeks.
  function parseAvailability(fields) {
    var f = fields || {};
    var dates = normaliseDateList(f.dates);
    if (dates.length) return { dates: dates, weeks: null, start: null, end: null };
    var weeks = normaliseWeekList(f.weeks);
    if (weeks.length) return { dates: null, weeks: weeks, start: null, end: null };
    return { dates: null, weeks: null, start: normaliseCode(f.startWeek), end: normaliseCode(f.endWeek) };
  }

  // `slot` is an ISO date (special date) or a week code. A dated product matches only its
  // listed ISO slots; a dated/ISO mismatch is 'unavailable', never not_yet/ended.
  function availabilityFor(availability, slot) {
    var a = availability || {};
    var isoSlot = typeof slot === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(slot);
    if (a.dates) return isoSlot && a.dates.indexOf(slot) !== -1 ? 'available' : 'unavailable';
    if (isoSlot) return 'unavailable';
    var weekCode = slot;
    if (a.weeks) {
      if (a.weeks.indexOf(weekCode) !== -1) return 'available';
      return weekCode < a.weeks[0] ? 'not_yet' : 'ended';
    }
    if (a.start && weekCode < a.start) return 'not_yet';
    if (a.end && weekCode > a.end) return 'ended';
    return 'available';
  }

  function availableWeeks(availability, horizon) {
    return (horizon || []).filter(function (w) {
      return availabilityFor(availability, w) === 'available';
    });
  }

  function intersectWeeks(lists) {
    if (!Array.isArray(lists) || !lists.length) return [];
    return lists.reduce(function (acc, list) {
      var set = Array.isArray(list) ? list : [];
      return acc.filter(function (w) { return set.indexOf(w) !== -1; });
    }, (lists[0] || []).slice());
  }

  // Sorted union of every availability's listed dates that are also in openISOs.
  function datesOffered(availabilities, openISOs) {
    var open = Array.isArray(openISOs) ? openISOs : [];
    var seen = {};
    (Array.isArray(availabilities) ? availabilities : []).forEach(function (a) {
      ((a && a.dates) || []).forEach(function (iso) {
        if (open.indexOf(iso) !== -1) seen[iso] = 1;
      });
    });
    return Object.keys(seen).sort();
  }

  // The next `count` delivery weeks, anchored to the week containing the soonest open
  // date. A partially open week (some dates past cutoff) always counts. A week with no
  // open date consumes a slot only when skipClosed is false.
  function horizonWeeks(openISODates, count, skipClosed) {
    var open = (openISODates || []).slice().sort();
    if (!open.length || !(count > 0)) return [];
    var withDates = {};
    open.forEach(function (iso) {
      var c = weekCodeOf(iso);
      if (c) withDates[c] = 1;
    });
    var first = weekCodeOf(open[0]);
    var p = parseWeekCode(first);
    if (!p) return [];
    var out = [];
    var cursor = weekStartUTC(p.year, p.week);
    for (var i = 0; i < 260 && out.length < count; i += 1) {
      var code = weekCodeOf(isoOfUTC(cursor));
      if (code && (!skipClosed || withDates[code])) out.push(code);
      cursor = new Date(cursor.getTime() + 7 * MS_PER_DAY);
    }
    return out;
  }

  return {
    menuSlot: menuSlot,
    specialCartNotice: specialCartNotice,
    deliveryWeekCode: deliveryWeekCode,
    deliveryWindowLabel: deliveryWindowLabel,
    deliveryDueLabel: deliveryDueLabel,
    deliveryBarDateLabel: deliveryBarDateLabel,
    deliveryCutoff: deliveryCutoff,
    parseWeekCode: parseWeekCode,
    weekCodeOf: weekCodeOf,
    weekBounds: weekBounds,
    specialDatesOf: specialDatesOf,
    isSpecialDate: isSpecialDate,
    specialDateOpen: specialDateOpen,
    specialDateLabel: specialDateLabel,
    shortDateLabel: shortDateLabel,
    specialDateTitle: specialDateTitle,
    selectionWindowLabel: selectionWindowLabel,
    slotOf: slotOf,
    shouldDropSpecialSelection: shouldDropSpecialSelection,
    datesOffered: datesOffered,
    parseAvailability: parseAvailability,
    availabilityFor: availabilityFor,
    availableWeeks: availableWeeks,
    intersectWeeks: intersectWeeks,
    horizonWeeks: horizonWeeks,
  };
});
